<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
/* global axios */
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import ShipmentTrackingModal from './ShipmentTrackingModal.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox, isAWhatsAppChannel } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value?.hmac_verified ?? false;
});

const currentContact = computed(() => {
  const contact = store.getters['contacts/getContact'](
    props.chat.meta?.sender?.id
  );
  return contact || {};
});

const formattedPhoneNumber = computed(() => {
  const name = currentContact.value.name || '';
  // Show the WhatsApp number this conversation actually came in on
  // (contact_inbox.source_id) rather than the contact's saved phone. The same
  // contact can write from several numbers — each its own conversation — and
  // the header should reflect which number this thread belongs to. Fall back to
  // the contact's saved phone when the source id isn't available.
  const sourceId = chatMetadata.value?.source_id;
  const phone =
    (isAWhatsAppChannel.value && sourceId) || currentContact.value.phone_number;

  if (
    isAWhatsAppChannel.value &&
    phone &&
    name !== phone &&
    !name.includes(phone)
  ) {
    let cleanPhone = String(phone).replace(/\D/g, '');
    if (cleanPhone.startsWith('2')) {
      cleanPhone = cleanPhone.substring(1);
    }
    return cleanPhone;
  }

  return null;
});

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

// Eltafouk: a comment thread has no ERP/customer/shipment context — the author
// is a Facebook commenter, not an order/customer. Hide that chrome here. The
// inbox `is_comment_inbox` flag is the durable signal; `comment_id` (written by
// n8n) corroborates it and can be momentarily absent right after creation.
const isCommentSection = computed(
  () =>
    Boolean(inbox.value?.is_comment_inbox) ||
    props.chat?.custom_attributes?.comment_id != null
);

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// Eltafouk: ERP context shown in the header. The bridge mirrors these onto the
// contact's additional_attributes; the conversation ContactPanel loads the full
// contact (contacts/show) so they're available here.
const ERP_BASE_URL = 'https://erp.eltafouk.com';

const erpCustomer = computed(
  () =>
    currentContact.value.additional_attributes?.erp_customer ||
    currentContact.value.identifier ||
    ''
);

const isErpLinked = computed(
  () =>
    currentContact.value.additional_attributes?.erp_link_status === 'linked' &&
    !!erpCustomer.value
);

const classificationChip = computed(() => {
  const cls =
    currentContact.value.additional_attributes?.custom_customer_classification;
  const map = {
    Student: {
      label: t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.STUDENT'),
      class: 'bg-n-blue-3 text-n-blue-11',
    },
    Teacher: {
      label: t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.TEACHER'),
      class: 'bg-n-violet-3 text-n-violet-11',
    },
    Library: {
      label: t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.LIBRARY'),
      class: 'bg-n-amber-3 text-n-amber-11',
    },
    Center: {
      label: t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.CENTER'),
      class: 'bg-n-teal-3 text-n-teal-11',
    },
    Other: {
      label: t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.OTHER'),
      class: 'bg-n-alpha-2 text-n-slate-11',
    },
  };
  return map[cls] || null;
});

// Teacher's subject(s). Lives on the ERP Customer (custom_teacher_subjects);
// shown only for teachers. The value reaches the contact via the bridge mirror
// (synced into additional_attributes) — until that sync lands the slot stays empty.
const teacherSubject = computed(() => {
  const aa = currentContact.value.additional_attributes;
  if (aa?.custom_customer_classification !== 'Teacher') return '';
  return aa?.custom_teacher_subjects || '';
});

const erpLabel = computed(() => t('CONVERSATION.HEADER.ERP_LABEL'));

// Open-in-ERP target (customer page) doubles as the "linked" badge.
const erpCustomerUrl = computed(() =>
  erpCustomer.value
    ? `${ERP_BASE_URL}/app/customer/${encodeURIComponent(erpCustomer.value)}`
    : ''
);

// Quick deep-links to the customer's orders / invoices / statement in ERP.
const erpLinks = computed(() => {
  const code = erpCustomer.value;
  if (!code) return [];
  const c = encodeURIComponent(code);
  return [
    {
      key: 'orders',
      short: t('CONVERSATION.HEADER.ERP_LINKS.ORDERS.SHORT'),
      label: t('CONVERSATION.HEADER.ERP_LINKS.ORDERS.LABEL'),
      icon: 'i-lucide-shopping-cart',
      url: `${ERP_BASE_URL}/app/sales-order?customer=${c}`,
    },
    {
      key: 'invoices',
      short: t('CONVERSATION.HEADER.ERP_LINKS.INVOICES.SHORT'),
      label: t('CONVERSATION.HEADER.ERP_LINKS.INVOICES.LABEL'),
      icon: 'i-lucide-receipt',
      url: `${ERP_BASE_URL}/app/sales-invoice?customer=${c}`,
    },
    {
      key: 'statement',
      short: t('CONVERSATION.HEADER.ERP_LINKS.STATEMENT.SHORT'),
      label: t('CONVERSATION.HEADER.ERP_LINKS.STATEMENT.LABEL'),
      icon: 'i-lucide-file-text',
      url: `${ERP_BASE_URL}/app/query-report/General%20Ledger?party_type=Customer&party=${c}`,
    },
  ];
});

const shipment = ref(null);
const isLoadingShipment = ref(false);
const isRefreshingShipment = ref(false);
// Contact id the currently loaded shipment belongs to, captured at load time so
// a refresh always targets the right contact even if the user switched chats.
const shipmentContactId = ref(null);
// Eltafouk: per-contact shipment cache. Fetching a shipment is a LIVE round-trip
// to ERPNext (up to ~15s); without this the pill blanked + re-queried on every
// open / re-evaluation, so it visibly vanished for several seconds each time.
// The cache lets us paint the last known pill instantly while a refresh runs in
// the background. Survives conversation switches (the header instance persists).
const shipmentCache = new Map();

const fetchShipments = async () => {
  const contactId = currentContact.value?.id;
  if (isCommentSection.value || !contactId || !isErpLinked.value) {
    shipment.value = null;
    shipmentContactId.value = null;
    return;
  }
  shipmentContactId.value = contactId;
  // Show the cached pill immediately (no blank) while we refresh in the background.
  if (shipmentCache.has(contactId)) {
    shipment.value = shipmentCache.get(contactId);
  }

  isLoadingShipment.value = true;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/${contactId}/shipments`
    );
    // The user may have switched contacts while this was in flight — drop a
    // stale response instead of painting it over the current contact's pill.
    if (currentContact.value?.id !== contactId) return;
    const rows = response.data?.rows || [];
    const next = rows.length > 0 ? rows[0] : null;
    shipment.value = next;
    shipmentCache.set(contactId, next);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch shipments:', error);
    // Keep the last known pill on a transient error instead of blanking.
  } finally {
    if (currentContact.value?.id === contactId) {
      isLoadingShipment.value = false;
    }
  }
};

const refreshShipment = async () => {
  if (!shipment.value || isRefreshingShipment.value) return;
  const contactId = shipmentContactId.value;
  if (!contactId) return;

  isRefreshingShipment.value = true;
  try {
    const shippingNumber = shipment.value.shipping_number;
    await axios.post(
      `/api/v1/accounts/${accountId.value}/contacts/${contactId}/refresh_shipment`,
      {
        shipping_number: shippingNumber,
      }
    );
    await fetchShipments();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to refresh shipment:', error);
  } finally {
    isRefreshingShipment.value = false;
  }
};

watch(
  () => currentContact.value?.id,
  newId => {
    if (newId) {
      // Paint this contact's cached pill (or blank if never loaded) before the
      // background refresh, so switching back to a chat never flashes empty.
      shipment.value = shipmentCache.has(newId)
        ? shipmentCache.get(newId)
        : null;
      fetchShipments();
    } else {
      shipment.value = null;
    }
  },
  { immediate: true }
);

watch(
  () => isErpLinked.value,
  linked => {
    if (linked) {
      // Refresh in the background — fetchShipments keeps the cached pill visible
      // meanwhile, so a transient link re-evaluation no longer blanks the pill.
      fetchShipments();
    } else {
      // Genuinely unlinked — hide the pill and drop the stale cache entry.
      shipment.value = null;
      const contactId = currentContact.value?.id;
      if (contactId) shipmentCache.delete(contactId);
    }
  }
);

// Full-tracking modal (opened by clicking the shipment pill).
const showTrackingModal = ref(false);
const openTrackingModal = () => {
  showTrackingModal.value = true;
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-14"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 gap-1.5 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1.5 p-0 m-0">
          <span
            class="text-sm font-semibold truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <span
            v-if="formattedPhoneNumber"
            class="inline-flex items-center gap-1 flex-shrink-0 ltr:pl-1.5 rtl:pr-1.5 ltr:border-l rtl:border-r border-n-strong leading-none"
            dir="ltr"
          >
            <span class="i-lucide-phone size-3 text-n-teal-10 flex-shrink-0" />
            <span
              class="text-[11px] font-semibold text-n-slate-11 tabular-nums"
            >
              {{ formattedPhoneNumber }}
            </span>
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-x-auto text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>

          <!-- Eltafouk: ERP customer classification chip -->
          <span
            v-if="classificationChip && !isCommentSection"
            class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0"
            :class="classificationChip.class"
          >
            {{ classificationChip.label }}
          </span>
          <span
            v-if="teacherSubject && !isCommentSection"
            v-tooltip.top="t('CONVERSATION.HEADER.TEACHER_SUBJECT')"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-n-alpha-2 text-n-slate-11 flex-shrink-0"
          >
            <span class="i-lucide-book-open size-3 flex-shrink-0" />
            {{ teacherSubject }}
          </span>

          <!-- Eltafouk: grouped ERP toolbar (open customer + orders / invoices / statement) -->
          <div
            v-if="erpCustomerUrl && !isCommentSection"
            class="inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-n-weak bg-n-alpha-1 flex-shrink-0"
          >
            <a
              v-tooltip.top="t('CONVERSATION.HEADER.OPEN_CUSTOMER_IN_ERP')"
              :href="erpCustomerUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-bold cursor-pointer transition-colors"
              :class="
                isErpLinked
                  ? 'bg-n-teal-3 text-n-teal-11 hover:brightness-95'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
            >
              <span
                v-if="isErpLinked"
                class="i-lucide-check size-3 flex-shrink-0"
              />
              {{ erpLabel }}
            </a>
            <span class="w-px h-4 bg-n-weak flex-shrink-0" />
            <a
              v-for="link in erpLinks"
              :key="link.key"
              v-tooltip.top="link.label"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 cursor-pointer transition-colors"
            >
              <span class="size-3.5" :class="link.icon" />
            </a>
          </div>

          <!-- Eltafouk: Shipment Tracking Pill -->
          <div
            v-if="shipment && !isCommentSection"
            class="inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-n-weak bg-n-alpha-1 flex-shrink-0"
          >
            <button
              v-tooltip.top="{
                content: t('CONVERSATION.HEADER.VIEW_FULL_TRACKING'),
                classes: 'text-xs max-w-xs',
              }"
              class="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-bold bg-n-blue-3 text-n-blue-11 hover:brightness-95 cursor-pointer transition"
              @click="openTrackingModal"
            >
              <span class="i-lucide-truck size-3 flex-shrink-0" />
              <span>{{
                shipment.status_display || shipment.status_ar || shipment.status
              }}</span>
              <span class="text-n-blue-9" dir="ltr">
                {{ t('CONVERSATION.HEADER.HASH')
                }}{{ shipment.shipping_number }}
              </span>
            </button>
            <button
              v-tooltip.top="t('CONVERSATION.HEADER.REFRESH_SHIPMENT')"
              class="inline-flex items-center justify-center size-7 rounded-md border border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-teal-3 hover:text-n-teal-11 hover:border-n-teal-6 cursor-pointer transition-colors"
              :disabled="isRefreshingShipment"
              @click="refreshShipment"
            >
              <span
                class="size-4 i-lucide-rotate-cw"
                :class="{ 'animate-spin': isRefreshingShipment }"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions
        :conversation-id="currentChat.id"
        :is-comment-section="isCommentSection"
      />
    </div>
    <ShipmentTrackingModal
      v-if="shipment && currentContact.id"
      v-model:show="showTrackingModal"
      :account-id="accountId"
      :contact-id="currentContact.id"
      :conversation-id="currentChat.id"
      :shipment="shipment"
      :can-reply="currentChat.can_reply"
    />
  </div>
</template>
